import React, { useState } from "react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import { ChevronDown } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { GuestSteppers } from "./GuestSteppers";

export const BookingFields = ({
  range, setRange, today, windowEnd, busyDates, availLoading, availError,
  adults, setAdults, childrenCount, setChildrenCount,
  maxAdults = 1, maxChildren = 0, childRuleText = "", childrenAllowed = true,
}) => {
  const [dateOpen, setDateOpen] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const guests = adults + childrenCount;

  const onSelect = (r) => {
    setRange(r);
    if (r?.from && r?.to && r.from.getTime() !== r.to.getTime()) setTimeout(() => setDateOpen(false), 250);
  };

  return (
    <div className="rounded-2xl border border-black/20 overflow-hidden" data-testid="booking-fields">
      <Popover open={dateOpen} onOpenChange={setDateOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="grid grid-cols-2 w-full text-left divide-x divide-black/20 hover:bg-paper transition-colors" data-testid="date-fields">
            <span className="px-4 py-3">
              <span className="block text-[10px] font-bold tracking-wider text-ink uppercase">Check-in</span>
              <span className={`block text-sm mt-0.5 truncate ${range?.from ? "text-ink font-medium" : "text-ink-faint"}`} data-testid="checkin-value">
                {range?.from ? format(range.from, "dd MMM yyyy") : "Add date"}
              </span>
            </span>
            <span className="px-4 py-3">
              <span className="block text-[10px] font-bold tracking-wider text-ink uppercase">Checkout</span>
              <span className={`block text-sm mt-0.5 truncate ${range?.to ? "text-ink font-medium" : "text-ink-faint"}`} data-testid="checkout-value">
                {range?.to ? format(range.to, "dd MMM yyyy") : "Add date"}
              </span>
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={8} className="w-auto p-3 rounded-2xl shadow-xl border-black/10" data-testid="date-popover">
          <div className="rdp-wrap" style={{ "--rdp-cell-size": "38px" }}>
            <DayPicker
              mode="range" selected={range} onSelect={onSelect}
              defaultMonth={range?.from || today} numberOfMonths={2}
              fromDate={today} toDate={windowEnd}
              disabled={[{ before: today }, ...busyDates]}
              data-testid="date-picker"
            />
          </div>
          <div className="flex items-center justify-between gap-4 px-1 pt-1">
            <p className="text-xs text-ink-faint">
              {availLoading ? "Loading availability…" : availError ? "Couldn't load availability — all dates shown." : "Greyed-out dates are already booked"}
            </p>
            {range?.from && (
              <button type="button" onClick={() => setRange(undefined)} className="text-xs font-semibold underline text-ink hover:text-brand shrink-0" data-testid="clear-dates">
                Clear dates
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Popover open={guestOpen} onOpenChange={setGuestOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="w-full flex items-center justify-between px-4 py-3 text-left border-t border-black/20 hover:bg-paper transition-colors" data-testid="guests-field">
            <span>
              <span className="block text-[10px] font-bold tracking-wider text-ink uppercase">Guests</span>
              <span className="block text-sm mt-0.5 text-ink font-medium" data-testid="guests-value">
                {guests} guest{guests !== 1 ? "s" : ""}{childrenCount > 0 ? ` · ${childrenCount} child${childrenCount !== 1 ? "ren" : ""}` : ""}
              </span>
            </span>
            <ChevronDown className={`w-4 h-4 text-ink transition-transform ${guestOpen ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={8} className="w-80 px-4 py-2 rounded-2xl shadow-xl border-black/10" data-testid="guests-popover">
          <GuestSteppers adults={adults} setAdults={setAdults} childrenCount={childrenCount} setChildrenCount={setChildrenCount} maxAdults={maxAdults} maxChildren={maxChildren} childRuleText={childRuleText} childrenAllowed={childrenAllowed} />
          <p className="text-xs text-ink-faint py-2.5 border-t border-black/5">This room takes up to {maxAdults} adult{maxAdults !== 1 ? "s" : ""}{maxChildren > 0 && childrenAllowed ? ` and ${maxChildren} child${maxChildren !== 1 ? "ren" : ""}` : ""}.</p>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default BookingFields;
